import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '@/hooks/useGameState';
import { GAME_CONSTANTS } from '@/types/game';

export const Minimap = () => {
  const { t } = useTranslation();
  const playerSnake = useGameStore((state) => state.playerSnake);
  const aiSnakes = useGameStore((state) => state.aiSnakes);

  // Map size in pixels
  const MAP_SIZE = 120;
  const WORLD_SIZE = GAME_CONSTANTS.WORLD_SIZE;

  // Convert world coordinates to minimap coordinates (2D projection - X and Z axes)
  const worldToMinimap = (x: number, z: number) => {
    const halfWorld = WORLD_SIZE / 2;
    const mapX = ((x + halfWorld) / WORLD_SIZE) * MAP_SIZE;
    const mapY = ((z + halfWorld) / WORLD_SIZE) * MAP_SIZE;
    return { x: mapX, y: mapY };
  };

  const playerPos = useMemo(() => {
    if (!playerSnake) return null;
    return worldToMinimap(playerSnake.position.x, playerSnake.position.z);
  }, [playerSnake?.position.x, playerSnake?.position.z]);

  const aiPositions = useMemo(() => {
    return aiSnakes
      .filter(snake => snake.isAlive)
      .map(snake => ({
        id: snake.id,
        ...worldToMinimap(snake.position.x, snake.position.z),
        color: snake.color,
      }));
  }, [aiSnakes]);

  if (!playerSnake) return null;

  return (
    <div 
      className="fixed top-4 left-4 z-40 pointer-events-none"
      style={{ width: MAP_SIZE, height: MAP_SIZE }}
    >
      {/* Map background */}
      <div 
        className="absolute inset-0 rounded-lg bg-black/70 border border-white/20 backdrop-blur-sm overflow-hidden"
      >
        {/* Grid lines for reference */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/50" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white/50" />
        </div>

        {/* AI snake dots */}
        {aiPositions.map(({ id, x, y, color }) => (
          <div
            key={id}
            className="absolute rounded-full"
            style={{
              left: x - 2,
              top: y - 2,
              width: 4,
              height: 4,
              backgroundColor: color,
              boxShadow: `0 0 4px ${color}`,
            }}
          />
        ))}

        {/* Player dot - larger and with pulse animation */}
        {playerPos && (
          <div
            className="absolute animate-pulse"
            style={{
              left: playerPos.x - 4,
              top: playerPos.y - 4,
              width: 8,
              height: 8,
            }}
          >
            {/* Outer glow ring */}
            <div 
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #00FFFF',
                opacity: 0.5,
              }}
            />
            {/* Inner dot */}
            <div 
              className="absolute inset-0 rounded-full"
              style={{
                backgroundColor: '#00FFFF',
                boxShadow: '0 0 8px #00FFFF, 0 0 16px #00FFFF',
              }}
            />
          </div>
        )}
      </div>

      {/* Label */}
      <div className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-white/50 font-mono">
        {t('minimap.label')}
      </div>
    </div>
  );
};
